import type { FieldDefinition } from "@estelle/domain";

/**
 * The fields the warehouse master offers to the filter builder, the column
 * chooser and the sort control.
 *
 * Order matters — the column chooser takes the first six as the default
 * set, and there are exactly six: a warehouse record has nothing left over
 * to make optional.
 */
export const WAREHOUSE_FIELDS: FieldDefinition[] = [
  { name: "name", label: "Name", type: "text", sortable: true },
  { name: "code", label: "Code", type: "text", sortable: true },
  { name: "city", label: "City", type: "text", sortable: true },
  { name: "state", label: "State", type: "text", sortable: true },
  { name: "isActive", label: "Active", type: "boolean" },
  { name: "fulfilsOrders", label: "Fulfils orders", type: "boolean" },
];

/**
 * The fields the Transfers list offers.
 *
 * `status` sits third rather than last: `draft` is the only status still
 * editable, and everything from `dispatched` onward has already moved stock
 * at the channel (FR-WHS-9) — that is the first thing worth reading about a
 * transfer, not an afterthought after the dates.
 */
export const TRANSFER_FIELDS: FieldDefinition[] = [
  { name: "reference", label: "Reference", type: "text", sortable: true },
  { name: "fromWarehouse", label: "From", type: "reference" },
  { name: "toWarehouse", label: "To", type: "reference" },
  {
    name: "status",
    label: "Status",
    type: "enum",
    sortable: true,
    options: [
      { value: "draft", label: "Draft" },
      { value: "dispatched", label: "Dispatched" },
      { value: "partially_received", label: "Partially received" },
      { value: "received", label: "Received" },
      { value: "cancelled", label: "Cancelled" },
    ],
  },
  { name: "createdAt", label: "Created", type: "date", sortable: true },
  { name: "dispatchedAt", label: "Dispatched", type: "date" },
];

/**
 * In Transit (FR-WHS-12).
 *
 * `ageDays` is the reason this screen exists — a transfer dispatched and
 * never received is invisible in both warehouses until something counts the
 * days. It is declared `number` so the filter builder offers the ordered
 * operators, which is what makes "everything older than 14 days" expressible.
 */
export const IN_TRANSIT_FIELDS: FieldDefinition[] = [
  { name: "reference", label: "Transfer", type: "text", sortable: true },
  { name: "fromWarehouse", label: "From", type: "text", sortable: true },
  { name: "toWarehouse", label: "To", type: "text", sortable: true },
  { name: "quantity", label: "Quantity", type: "number", sortable: true },
  { name: "dispatchedAt", label: "Dispatched", type: "date", sortable: true },
  { name: "ageDays", label: "Age (days)", type: "number", sortable: true },
];
